import { IFormRender } from '@busybusy/webapp-react-ui';
import { IBitwisePayload } from 'components/foundation/BitwiseGroup/BitwiseGroup';
import { bitwisePayloadMasker } from 'components/foundation/BitwiseGroup/util/Util';
import BitwiseGroupFormField from 'components/foundation/form-fields/BitwiseGroupFormField/BitwiseGroupFormField';
import usePhotosViewCollectionPermissionEnabled from 'containers/photos/hooks/usPhotosViewCollectionPermissionEnabled/usePhotosViewCollectionPermissionEnabled';
import HeaderText from 'containers/settings/components/HeaderText/HeaderText';
import SectionHeader from 'containers/settings/components/SectionHeader/SectionHeader';
import PositionProgressFormFields from 'containers/settings/positions/components/PositionProgressFormFields';
import PositionsViewActivityReportFormFields from 'containers/settings/positions/components/PositionsViewActivityReportFormFields';
import { defaultBitwisePayloads } from 'containers/settings/positions/util/constants';
import { useFeatureFlags } from 'utils/features';
import { t } from 'utils/localize';
import { ICreatePositionsFormData } from '../CreatePositionForm';

export interface ICreatePositionSupervisionSectionProps {
  form: IFormRender<ICreatePositionsFormData>;
}

interface ISupervisorSetting {
  name: string;
  title: string;
  detail: string;
  bitwisePayloads: IBitwisePayload[];
  onChange?: (value: number) => void;
}

const CreatePositionSupervisionSection = ({ form }: ICreatePositionSupervisionSectionProps) => {
  const progressReportIsEnabled = useFeatureFlags('WORK_ACCOMPLISHED');
  const isLocationFlaggingEnabled = useFeatureFlags('LOCATION_FLAGGING');
  const isPerDiemEnabled = useFeatureFlags('PER_DIEM');
  const photosViewCollectionPermissionEnabled = usePhotosViewCollectionPermissionEnabled();

  const supervisionSettings: ISupervisorSetting[] = [];

  supervisionSettings.push({
    name: 'manageEmployees',
    title: t('Manage Employees for') + ':',
    detail: t('View, create, edit, and archive employees; set wages, position, etc.'),
    bitwisePayloads: defaultBitwisePayloads,
  });

  supervisionSettings.push({
    name: 'timeEvents',
    title: t('Track Time for') + ':',
    detail: t('Clock in, clock out, start/end breaks, and switch.'),
    bitwisePayloads: bitwisePayloadMasker(defaultBitwisePayloads, form.state.data.manageTimeEntries ?? 0),
  });

  supervisionSettings.push({
    name: 'manageTimeEntries',
    title: t('Edit Time Entries for') + ':',
    detail: t(
      'View, create, edit, and delete time entries; clock in at, clock out at, switch at; add, edit, delete, and approve time off.'
    ),
    bitwisePayloads: defaultBitwisePayloads,
    onChange: (value: number) => {
      const current = form.state.data.timeEvents;
      const newValue = current | value;
      if (newValue !== current) {
        form.handleChange('timeEvents', newValue);
      }
    },
  });

  supervisionSettings.push({
    name: 'manageTimeOff',
    title: t('Manage Time Off for') + ':',
    detail: t('Can add, edit, delete, and approve time off.'),
    bitwisePayloads: defaultBitwisePayloads,
  });

  supervisionSettings.push({
    name: 'manageMediaCollection',
    title: t('Manage Photos Posted by') + ':',
    detail: t('Can edit and delete photos, notes, comments, and tags uploaded by other employees.'),
    bitwisePayloads: defaultBitwisePayloads,
    onChange: (value: number) => {
      const currentViewPhotos = form.state.data.viewMediaCollection;
      const newValue = currentViewPhotos | value;
      if (newValue !== currentViewPhotos) {
        form.handleChange('viewMediaCollection', newValue);
      }
    },
  });

  if (photosViewCollectionPermissionEnabled) {
    supervisionSettings.push({
      name: 'viewMediaCollection',
      title: t('View Photos Posted by') + ':',
      detail: t('Can view photos and notes.'),
      bitwisePayloads: bitwisePayloadMasker(defaultBitwisePayloads, (form.state.data.manageMediaCollection ?? 0) | 1),
    });
  }

  supervisionSettings.push({
    name: 'memberLock',
    title: t('Manage Lock Dates for') + ':',
    detail: t('View, set, edit, and remove employee lock dates.'),
    bitwisePayloads: defaultBitwisePayloads,
  });

  supervisionSettings.push({
    name: 'viewSchedule',
    title: t('View Schedules for') + ':',
    detail: t('View schedule items.'),
    bitwisePayloads: bitwisePayloadMasker(defaultBitwisePayloads, form.state.data.manageSchedule ?? 0),
  });

  supervisionSettings.push({
    name: 'manageSchedule',
    title: t('Manage Schedule for') + ':',
    detail: t('View, create, edit, delete schedule items.'),
    bitwisePayloads: defaultBitwisePayloads,
    onChange: (value: number) => {
      const currentViewSchedule = form.state.data.viewSchedule;
      const newValue = currentViewSchedule | value;
      if (newValue !== currentViewSchedule) {
        form.handleChange('viewSchedule', newValue);
      }
    },
  });

  if (isLocationFlaggingEnabled) {
    supervisionSettings.push({
      name: 'manageGpsApproval',
      title: t('Authorize GPS for') + ':',
      detail: t('Can approve or unauthorize employee locations outside project boundaries.'),
      bitwisePayloads: defaultBitwisePayloads,
    });
  }

  if (isPerDiemEnabled) {
    supervisionSettings.push({
      name: 'managePerDiem',
      title: t('Manage Per Diem for') + ':',
      detail: t('Can add, edit, delete, and approve per diem for others.'),
      bitwisePayloads: defaultBitwisePayloads,
    });
  }

  return (
    <>
      <SectionHeader className="mb-4">{t('Supervision Settings')}</SectionHeader>

      {supervisionSettings.map(({ name, title, detail, bitwisePayloads: payloads, onChange }) => (
        <>
          <HeaderText title={title}>{detail}</HeaderText>
          <BitwiseGroupFormField className="mt-2" name={name} payloads={payloads} form={form} onChange={onChange} />
        </>
      ))}

      {progressReportIsEnabled && <PositionProgressFormFields form={form} />}

      <PositionsViewActivityReportFormFields form={form} />
    </>
  );
};

export default CreatePositionSupervisionSection;
